import { useMemo, useState } from "react"
import { Link, router } from "@inertiajs/react"
import { Book, Check, ChevronRight, Clock, Inbox, MessageSquare, Plus, Scan, Search, Send, X } from "lucide-react"

import AppShell from "@/components/tuw/AppShell"
import { Avatar } from "@/components/tuw/Avatar"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { csrfToken } from "@/lib/orderActions"
import { cn } from "@/lib/utils"

// Kind -> glyph. These are the three sidebar icons the dead Invites / Samples /
// Out of Scope items used to carry; they move here, onto the rows themselves.
const KIND_ICON = { invite: Send, sample: Book, outOfScope: Scan }

const CHIPS = [
  { key: "all", label: "All", countKey: "all" },
  { key: "invite", label: "Invitations", countKey: "invites" },
  { key: "sample", label: "Samples", countKey: "samples" },
  { key: "outOfScope", label: "Out of scope", countKey: "outOfScope" },
]

// Cancelling is irreversible and the freelancer is told, so each one confirms.
// The copy names what the customer loses, not what the system does.
const CANCEL_COPY = {
  invite: {
    title: "Withdraw this invitation?",
    body: "The freelancer will be told the offer is gone. You can invite them again afterwards, but the message you sent with it is not kept.",
  },
  sample: {
    title: "Cancel this sample request?",
    body: "The writer will no longer be able to accept it, and you will not be charged. Asking again starts a new request.",
  },
  outOfScope: {
    title: "Cancel this out-of-scope request?",
    body: "The freelancer will no longer be able to accept it, and you will not be charged. Asking again starts a new request.",
  },
}

function StatusPill({ state, label }) {
  const Icon = state === "accepted" ? Check : state === "declined" ? X : Clock

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-[11px] py-[5px] text-[11.5px] font-semibold whitespace-nowrap",
        state === "waiting" && "bg-progress/15 text-progress",
        state === "accepted" && "bg-done/15 text-done",
        state === "declined" && "bg-secondary text-hold"
      )}
    >
      <Icon className="size-3" strokeWidth={2.4} />
      {label}
    </span>
  )
}

function RequestCard({ item, onCancel }) {
  const Icon = KIND_ICON[item.kind] ?? Send
  const waiting = item.state === "waiting"

  const message = () => {
    router.post(item.messagePath, {}, { headers: { "X-CSRF-Token": csrfToken() } })
  }

  return (
    // Same card grid as Approvals: below 641px the action cluster drops onto its
    // own full-width row rather than competing with the text for the 1fr track.
    <div
      className={cn(
        "grid grid-cols-[48px_1fr] items-center gap-[18px] rounded-2xl border border-border bg-card p-[18px] transition-[border-color,box-shadow] max-[641px]:gap-3.5 max-[641px]:p-3.5 hover:border-line-2 hover:shadow-[0_1px_3px_rgba(0,0,0,0.04)] lg:grid-cols-[48px_1fr_auto]",
        waiting && "border-l-[3px] border-l-progress"
      )}
      data-testid="request-card"
    >
      <Avatar initials={item.freelancer.initials} name={item.freelancer.name} src={item.freelancer.avatarUrl} size={48} />

      <div className="min-w-0">
        <div className="mb-[5px] inline-flex items-center gap-1.5 text-[10.5px] font-bold tracking-[0.06em] text-ink-3 uppercase">
          <Icon className="size-3" strokeWidth={2} />
          {item.kindLabel}
        </div>
        <h3 className="font-display text-[15.5px] leading-tight font-semibold tracking-[-0.2px]">
          {item.freelancer.slug ? (
            <Link href={`/freelancers/${item.freelancer.slug}`} className="hover:text-primary">
              {item.freelancer.name}
            </Link>
          ) : (
            item.freelancer.name
          )}
        </h3>
        <div className="mt-[3px] flex flex-wrap items-center gap-[7px] text-[12px] text-ink-3">
          {item.meta.map((bit, i) => (
            <span key={`${bit}-${i}`} className="flex items-center gap-[7px]">
              {i > 0 && <span className="text-line-2">·</span>}
              <span className={bit.startsWith("#") ? "font-display" : undefined}>{bit}</span>
            </span>
          ))}
        </div>
        <p className="mt-[7px] max-w-[54ch] text-[12.5px] leading-[1.5] text-muted-foreground">{item.desc}</p>
      </div>

      <div
        className="col-start-2 flex w-full items-center justify-between gap-3 max-[641px]:flex-col max-[641px]:items-start max-[641px]:gap-2.5 lg:col-start-3 lg:w-auto lg:flex-col lg:items-end lg:gap-[11px]"
        data-testid="request-actions"
      >
        <StatusPill state={item.state} label={item.statusLabel} />

        <div className="flex items-center gap-2 max-[641px]:w-full max-[641px]:flex-wrap">
          {item.messagePath && (
            <Button variant="outline" size="sm" className="gap-1.5 max-[641px]:flex-auto" onClick={message}>
              <MessageSquare className="size-3.5" strokeWidth={2} />
              Message
            </Button>
          )}
          {item.cancelPath && (
            <Button
              variant="outline"
              size="sm"
              className="max-[641px]:flex-auto"
              onClick={() => onCancel(item)}
            >
              {item.cancelLabel}
            </Button>
          )}
          {item.projectHref && (
            <Button asChild size="sm" className="gap-1.5 font-semibold max-[641px]:flex-auto">
              <Link href={item.projectHref}>
                Open project
                <ChevronRight className="size-3.5" strokeWidth={2.4} />
              </Link>
            </Button>
          )}
          {/* Only a declined row offers this. An accepted one whose order has
              not materialised yet shows nothing rather than telling the customer
              to go find somebody else. */}
          {item.state === "declined" && (
            <Button asChild variant="outline" size="sm" className="max-[641px]:flex-auto">
              <Link href="/find-freelancers">Find someone else</Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

function Group({ label, count, live, children }) {
  return (
    <div className="flex flex-col gap-[11px]">
      <div className="mt-1.5 mb-px flex items-center gap-[9px] px-0.5 text-[11px] font-bold tracking-[0.08em] text-ink-3 uppercase">
        {label}
        <span
          className={cn(
            "inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] tracking-normal",
            live ? "bg-[var(--red-soft)] text-primary" : "bg-secondary text-muted-foreground"
          )}
        >
          {count}
        </span>
      </div>
      {children}
    </div>
  )
}

// UPDA-74 — Requests: everything this customer asked a freelancer for, in one
// place. Replaces the three href-less sidebar items (Invites / Samples / Out of
// Scope) that named legacy screens Atrium never rebuilt.
//
// All three are the same question, so they share one list: who you asked, what
// for, and what came back. "Waiting on them" is the half that had nowhere to
// live — until this screen there was no way to see an unanswered request, let
// alone cancel it. Once a freelancer accepts, the work becomes an ordinary
// project, so accepted rows hand off to /projects rather than duplicating it.
export default function Requests({ requests }) {
  const { items, counts, pendingCount, answeredCount, answeredDays } = requests
  const [filter, setFilter] = useState("all")
  const [confirm, setConfirm] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const visible = useMemo(
    () => (filter === "all" ? items : items.filter((item) => item.kind === filter)),
    [items, filter]
  )

  const waiting = visible.filter((item) => item.state === "waiting")
  const answered = visible.filter((item) => item.state !== "waiting")

  const cancel = (item) => {
    setSubmitting(true)
    router.delete(item.cancelPath, {
      headers: { "X-CSRF-Token": csrfToken() },
      preserveScroll: true,
      onFinish: () => {
        setSubmitting(false)
        setConfirm(null)
      },
    })
  }

  const subtitle =
    items.length === 0
      ? "You haven't asked a freelancer for anything yet."
      : [
          pendingCount > 0
            ? `${pendingCount} waiting on a freelancer`
            : "Nothing is waiting on a freelancer",
          answeredCount > 0 ? `${answeredCount} answered in the last ${answeredDays} days` : null,
        ]
          .filter(Boolean)
          .join(" · ")

  return (
    <>
      <div className="flex flex-col gap-5 px-[26px] pt-6 pb-12">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-[-0.4px]">Requests</h1>
            <p className="mt-1 text-[13px] text-ink-3">{subtitle}</p>
          </div>
          {/* Every request — an invitation, a sample, extra hours — is made to a
              freelancer from their profile or the roster, so a new one starts
              there (UPDA-99). */}
          <Button asChild className="shrink-0 gap-1.5 font-semibold">
            <Link href="/find-freelancers">
              <Plus className="size-4" strokeWidth={2.2} />
              New request
            </Link>
          </Button>
        </div>

        {items.length > 0 && (
          <div className="flex flex-wrap gap-[7px]">
            {CHIPS.filter((chip) => chip.key === "all" || counts[chip.countKey] > 0).map((chip) => {
              const active = filter === chip.key
              return (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => setFilter(chip.key)}
                  className={cn(
                    "flex items-center gap-[7px] rounded-[9px] border px-[13px] py-[7px] text-[12.5px] font-semibold whitespace-nowrap transition-colors",
                    active
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card text-muted-foreground hover:bg-secondary"
                  )}
                >
                  {chip.label}
                  <span className={cn("text-[11px] font-bold", active ? "text-white/80" : "text-ink-3")}>
                    {counts[chip.countKey]}
                  </span>
                </button>
              )
            })}
          </div>
        )}

        {items.length === 0 ? (
          <div className="grid place-items-center rounded-2xl border border-border bg-card py-20 text-center">
            <Inbox className="size-9 text-ink-3" strokeWidth={1.6} />
            <h3 className="mt-4 font-display text-base font-bold">No open requests</h3>
            <p className="mt-1.5 max-w-[380px] text-[13px] text-ink-3">
              When you invite a freelancer to a project, ask for a sample, or buy extra hours, it waits
              here until they answer.
            </p>
            <Button asChild className="mt-5 gap-1.5 font-semibold">
              <Link href="/find-freelancers">
                <Search className="size-4" strokeWidth={2.2} />
                Find a freelancer
              </Link>
            </Button>
          </div>
        ) : (
          <>
            {waiting.length > 0 && (
              <Group label="Waiting on them" count={waiting.length} live>
                {waiting.map((item) => (
                  <RequestCard key={item.id} item={item} onCancel={setConfirm} />
                ))}
              </Group>
            )}
            {answered.length > 0 && (
              <Group label="Answered" count={answered.length}>
                {answered.map((item) => (
                  <RequestCard key={item.id} item={item} onCancel={setConfirm} />
                ))}
              </Group>
            )}
            {visible.length === 0 && (
              <p className="py-10 text-center text-[13px] text-ink-3">No requests of this kind.</p>
            )}
          </>
        )}
      </div>

      <Dialog open={confirm != null} onOpenChange={(open) => !open && setConfirm(null)}>
        <DialogContent className="sm:max-w-[440px]">
          {confirm && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">
                  {CANCEL_COPY[confirm.kind]?.title ?? "Cancel this request?"}
                </DialogTitle>
                <DialogDescription className="text-[13px] leading-relaxed">
                  {CANCEL_COPY[confirm.kind]?.body}
                </DialogDescription>
              </DialogHeader>
              <div className="rounded-xl border border-border bg-accent px-4 py-3 text-[13px]">
                <div className="font-display font-semibold">{confirm.freelancer.name}</div>
                <div className="mt-0.5 text-ink-3">{confirm.meta.join(" · ")}</div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirm(null)} disabled={submitting}>
                  Keep it
                </Button>
                <Button
                  variant="destructive"
                  className="font-semibold"
                  onClick={() => cancel(confirm)}
                  disabled={submitting}
                >
                  {submitting ? "Cancelling…" : confirm.cancelLabel}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

Requests.layout = (page) => (
  <AppShell active="Requests" crumb={["Requests"]}>
    {page}
  </AppShell>
)
